/**
 * Images for the canvas, and the one rule about them.
 *
 * ## Everything drawn on the studio canvas must be CORS-clean
 *
 * `canvas.captureStream()` throws `SecurityError` on a tainted canvas, and a
 * single `drawImage` of a cross-origin image with no `crossOrigin` attribute
 * taints it permanently. Artwork comes from the media CDN, so *every* image the
 * compositor touches is cross-origin - which means one forgotten attribute
 * anywhere takes the whole broadcast down, not just that one picture.
 *
 * That is why loading goes through here rather than through `new Image()` at
 * the call site. There is no way to ask this module for an image that would
 * poison the canvas.
 *
 * The CDN echoes the requesting origin (see `workers/media/src/index.js`), so
 * `anonymous` is enough; `use-credentials` would additionally require the
 * response to allow credentials for this exact origin and buys nothing here.
 *
 * ## Cached, because the compositor asks thirty times a second
 *
 * The render loop is synchronous - it cannot await a decode - so it asks for
 * whatever is ready and skips what is not. The cache is what turns "ask every
 * frame" into one network request, and the null entries are what stop a broken
 * URL being retried thirty times a second forever.
 */

/** Resolved images, plus nulls for the ones that will never load. */
const cache = new Map<string, HTMLImageElement | null>();
const pending = new Set<string>();

/**
 * The image for a URL if it is decoded, otherwise null - and start loading it.
 *
 * Deliberately not a promise: callers are inside a `requestAnimationFrame` and
 * the correct behaviour for a picture that has not arrived is to draw the frame
 * without it, not to drop the frame.
 */
export function image(url: string | null | undefined): HTMLImageElement | null {
  if (!url) return null;

  const hit = cache.get(url);

  if (hit !== undefined) return hit;
  if (pending.has(url)) return null;

  pending.add(url);

  const img = new Image();

  // The line the whole studio depends on. See above.
  img.crossOrigin = 'anonymous';

  img.onload = () => {
    pending.delete(url);
    cache.set(url, img);
  };
  img.onerror = () => {
    pending.delete(url);
    // Remembered as a failure so the loop stops asking. A broadcast missing one
    // piece of artwork is a broadcast; one retrying a 404 every frame is not.
    cache.set(url, null);
  };

  img.src = corsUrl(url);

  return null;
}

/**
 * The same image, at a URL only ever requested in CORS mode.
 *
 * ## Why this is not paranoia
 *
 * The rest of the app draws artwork in plain `<img>` tags, which send no
 * `Origin` and get a response with no `Access-Control-Allow-Origin` on it. The
 * CDN sets `Vary: Origin`, so a compliant cache would treat the studio's
 * credentialless request as a miss and refetch - but Chrome's memory cache
 * hands back the non-CORS copy, the load fails, and the card silently loses its
 * artwork for exactly the tracks the host has already seen elsewhere on the
 * site. Which is most of them.
 *
 * A fixed parameter rather than a random one: this still caches, across tracks
 * and across sessions. It buys a separate cache entry, not a fresh download
 * every frame.
 *
 * The CDN ignores query parameters it does not recognise, so the bytes are the
 * same ones.
 */
function corsUrl(url: string): string {
  return url.includes('?') ? `${url}&canvas=1` : `${url}?canvas=1`;
}
